/**
 * Lop goi API cua lo de ba mien (`/v1/lode/*`).
 *
 * Tach khoi `lib/api.ts` chu khong noi them vao do: `lottery` trong tep kia la san
 * pham xo so MOT nha khai thac cua `docs/55`, chay song song va khong dung chung bang
 * nao voi lo de. Gop hai thu vao mot doi tuong `api` se lam nguoi doc tuong chung la
 * mot san pham co hai che do.
 *
 * Moi kieu du lieu duoi day bam theo dung phan hoi cua `src/http/routes/lode.routes.ts`.
 * Tien te di qua day duoi dang `MoneyDto` da dinh dang san tu may chu (QT-02: so lon
 * la CHUOI, khong bao gio la `number`).
 */

import { request } from './api';
import type { MoneyDto } from './money';

export type LodeRegion = 'MB' | 'MT' | 'MN';

/** Dang gop theo dai - dung `draw_ids` thay cho `draw_id`. */
export const LODE_MULTI_DRAW_BET_TYPES = ['BAO_LO'] as const;

export interface LodeOpenDraw {
  id: string;
  draw_time: string;
  closes_at: string;
  status: string;
}

export interface LodeStation {
  code: string;
  region: LodeRegion;
  name: string;
  draw_time_utc: string;
  /** `null` khi dai dang bat nhung chua co ky nao mo ban. */
  open_draw: LodeOpenDraw | null;
}

export interface LodeFastDraw {
  id: string;
  region_sim: LodeRegion;
  draw_time: string;
  opens_at: string;
  closes_at: string;
  /** `SCHEDULED` = da xep hang nhung chua toi gio, hien de dem nguoc chu khong dat duoc. */
  status: 'OPEN' | 'SCHEDULED';
}

export interface LodeOddsRow {
  bet_type: string;
  payout_per_point: MoneyDto;
  point_price: MoneyDto;
  odds_table_version: number;
  /** Gia rieng nha khai thac dat cho chinh nguoi choi nay. */
  custom?: boolean;
  /** STATION = dai nay co muc tra rieng, khac muc chung cua mien. */
  scope?: 'REGION' | 'STATION';
}

/**
 * Mot dong tren phieu cuoc.
 *
 * `draw_id` va `draw_ids` loai tru nhau: `BAO_LO` gop nhieu dai nen dung `draw_ids`
 * (2-10 dai, khong trung), moi dang con lai dung `draw_id`. May chu tu choi neu dung
 * nham truong, nen giao dien khong duoc doan thay.
 */
export interface LodeLineInput {
  draw_id?: string;
  draw_ids?: string[];
  bet_type: string;
  numbers: number[];
  /** So NUA diem: 10 = 5 diem. Toi thieu 2. */
  points_x2: number;
}

export interface LodeTicketCreated {
  ticket_id: string;
  stake: MoneyDto;
}

export interface LodeTicketRow {
  id: string;
  sku_code: string;
  stake: MoneyDto;
  status: string;
  server_time: string;
}

export interface LodeTicketLine {
  id: string;
  draw_id: string;
  draw_status: string;
  bet_type: string;
  numbers: number[];
  points_x2: number;
  stake: MoneyDto;
  /** LDE-R05: ty le da CHUP luc mua, khong phai bang hien hanh. */
  odds_snapshot: {
    payout_per_point: MoneyDto;
    point_price: MoneyDto;
    odds_table_version: number;
  };
  hits: number | null;
  payout: MoneyDto | null;
  status: string;
}

export interface LodeTicketDetail {
  id: string;
  sku_code: string;
  stake: MoneyDto;
  status: string;
  server_time: string;
  resettled_count: number;
  lines: LodeTicketLine[];
}

/** Du lieu de nguoi choi TU bam lai SHA-256 va doi chieu (LDE-R11). */
export interface LodeVerification {
  draw_id: string;
  status: string;
  result_hash: string | null;
  prizes: number[] | null;
  commitment: {
    chain_terminal_hash: string | null;
    chain_index: number | null;
    server_seed_hash: string;
    published_at: string;
    revealed_seed: string | null;
    revealed_at: string | null;
  } | null;
}

export const lodeApi = {
  stations: () => request<{ stations: LodeStation[] }>('/v1/lode/stations'),

  fastDraws: () => request<{ draws: LodeFastDraw[] }>('/v1/lode/fast-draws'),

  /**
   * `stations` co ma dai nao thi phan hoi kem `by_station` cho dung nhung dai do - phieu
   * cuoc chon nhieu dai mot luc nen phai lay het trong mot lan goi.
   */
  odds: (region: LodeRegion, stations: readonly string[] = []) =>
    request<{
      region: LodeRegion;
      odds: LodeOddsRow[];
      by_station?: Record<string, LodeOddsRow[]>;
    }>(
      `/v1/lode/odds?region=${region}${stations.length > 0 ? `&stations=${encodeURIComponent(stations.join(','))}` : ''}`,
    ),

  /** ID-01: bat buoc khoa bat bien - goi lai cung khoa tra ve dung ve cu. */
  buy: (lines: LodeLineInput[], key: string) =>
    request<LodeTicketCreated>('/v1/lode/tickets', {
      method: 'POST',
      body: { lines },
      idempotencyKey: key,
    }),

  tickets: () => request<{ tickets: LodeTicketRow[] }>('/v1/lode/tickets'),

  ticket: (ticketId: string) => request<LodeTicketDetail>(`/v1/lode/tickets/${ticketId}`),

  verify: (drawId: string) => request<LodeVerification>(`/v1/lode/draws/${drawId}/verify`),
};
